import { Dialog } from '@headlessui/react';
import {
  ArrowsRightLeftIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  SparklesIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import BillingStatusBadge from '../components/billing/BillingStatusBadge';
import Skeleton from '../components/common/Skeleton';
import ContextHelpButton from '../components/help/ContextHelpButton';
import ContextHelpDrawer from '../components/help/ContextHelpDrawer';
import { MY_SUBSCRIPTION_HELP_SECTIONS } from '../components/help/waiterCallHelpContent';
import { useApiClient } from '../lib/api';
import { useNotification } from '../providers/notification-provider';
import { extractApiErrorMessage } from '../utils/apiErrorUtils';
import { cycleLabel, formatDate, formatMoney } from '../utils/billingUtils';

const usageBarColor = (percent) => {
  if (percent >= 90) return 'bg-rose-500';
  if (percent >= 70) return 'bg-amber-500';
  return 'bg-indigo-600';
};

const originLabel = (origin) => {
  if (origin === 'self_service') return 'Self-service';
  if (origin === 'manual') return 'Manual';
  if (origin === 'migration') return 'Migração';
  return origin ?? '-';
};

const providerLabel = (provider) => {
  if (provider === 'asaas') return 'Asaas';
  if (provider === 'manual') return 'Manual';
  return provider ?? '-';
};

const canChangePlan = (status) => ['active', 'pending_payment', 'past_due'].includes(status);

const getDaysUntil = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const diff = date.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0);
  return Math.ceil(diff / 86400000);
};

const getStatusInsight = (detail) => {
  if (detail.status === 'cancelled') {
    return {
      tone: 'muted',
      title: 'Assinatura cancelada',
      description: 'O acesso foi encerrado e os dados permanecem apenas durante o período de retenção.',
    };
  }

  if (detail.status === 'suspended') {
    return {
      tone: 'danger',
      title: 'Acesso suspenso',
      description: 'Regularize a cobrança para restabelecer o uso do WaiterCall.',
    };
  }

  if (detail.status === 'past_due') {
    return {
      tone: 'warning',
      title: 'Cobrança em atraso',
      description: 'Há risco de suspensão caso a pendência não seja regularizada.',
    };
  }

  if (detail.isWithinTrial) {
    return {
      tone: 'info',
      title: 'Avaliação gratuita ativa',
      description: `Sem cobrança até ${formatDate(detail.trialEndsAt)}. Use este período para validar mesas, restaurantes e equipe.`,
    };
  }

  return {
    tone: 'success',
    title: 'Operação regular',
    description: 'Plano ativo, consumo monitorado e assinatura apta para operação normal.',
  };
};

const toneStyles = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  info: 'border-indigo-200 bg-indigo-50 text-indigo-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  danger: 'border-rose-200 bg-rose-50 text-rose-900',
  muted: 'border-slate-200 bg-slate-50 text-slate-800',
};

const MySubscriptionPage = () => {
  const api = useApiClient();
  const { showToast, showConfirm } = useNotification();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(MY_SUBSCRIPTION_HELP_SECTIONS[0].key);

  const loadDetail = useCallback(async () => {
    setError(false);
    try {
      const response = await api.get('/api/v1/billing/current/detail');
      setDetail(response.data || null);
    } catch {
      setError(true);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await loadDetail();
      if (!mounted) return;
    };
    run();
    return () => { mounted = false; };
  }, [loadDetail]);

  const openChangePlan = async () => {
    if (loadingPlans) return;
    setChangePlanOpen(true);
    setLoadingPlans(true);
    setSelectedPlanId(null);
    try {
      const response = await api.get('/api/v1/billing/current/available-plans');
      setAvailablePlans(response.data || []);
    } catch {
      showToast('Erro ao carregar planos disponíveis.', 'error');
      setAvailablePlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const confirmChangePlan = async () => {
    if (!selectedPlanId || changingPlan) return;

    const plan = availablePlans.find((p) => p.id === selectedPlanId);
    if (!plan) return;

    const confirmed = await showConfirm(
      `Deseja alterar para o plano ${plan.landingLabel || plan.name} (${formatMoney(plan.priceAmount, plan.currency)})?`
    );
    if (!confirmed) return;

    setChangingPlan(true);
    try {
      const response = await api.put('/api/v1/billing/current/plan', { planId: selectedPlanId });
      setDetail(response.data);
      setChangePlanOpen(false);
      showToast('Plano alterado com sucesso.', 'success');
    } catch (err) {
      showToast(extractApiErrorMessage(err, 'Erro ao alterar o plano.'), 'error');
    } finally {
      setChangingPlan(false);
    }
  };

  const handleCancel = async () => {
    if (cancelling) return;

    const confirmMessage = detail.isWithinTrial
      ? 'Você está no período de avaliação gratuita. Nenhuma cobrança foi realizada. Deseja cancelar?'
      : 'Tem certeza que deseja cancelar a assinatura? O acesso ao sistema será encerrado.';
    const confirmed = await showConfirm(confirmMessage);
    if (!confirmed) return;

    setCancelling(true);
    try {
      const response = await api.post('/api/v1/billing/current/cancel', { reason: null });
      const msg = response.data?.withinTrial
        ? 'Assinatura cancelada durante a avaliação gratuita. Nenhuma cobrança foi realizada.'
        : response.data?.refunded
          ? 'Assinatura cancelada. O estorno do último pagamento será processado.'
          : 'Assinatura cancelada.';
      showToast(msg, 'success');
      await loadDetail();
    } catch (err) {
      showToast(extractApiErrorMessage(err, 'Erro ao cancelar a assinatura.'), 'error');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <SubscriptionLoadingState />;
  }

  if (error && !detail) {
    return (
      <SubscriptionFeedbackState
        title="Erro ao carregar a assinatura"
        description="Não foi possível obter os dados da assinatura. Verifique sua conexão e tente novamente."
        icon={XCircleIcon}
        tone="danger"
        actionLabel="Tentar novamente"
        onAction={() => { setLoading(true); loadDetail(); }}
      />
    );
  }

  if (!detail) {
    return (
      <SubscriptionFeedbackState
        title="Assinatura não encontrada"
        description="Nenhuma assinatura ativa foi localizada para esta empresa."
        icon={ExclamationTriangleIcon}
        tone="muted"
      />
    );
  }

  const tablePercent = detail.tableLimit > 0 ? Math.round(((detail.tablesUsed ?? 0) / detail.tableLimit) * 100) : 0;
  const restaurantPercent = detail.restaurantLimit > 0 ? Math.round(((detail.restaurantsUsed ?? 0) / detail.restaurantLimit) * 100) : 0;
  const isCancelled = detail.status === 'cancelled';
  const statusInsight = getStatusInsight(detail);
  const daysUntilDue = getDaysUntil(detail.nextDueAt);
  const planName = detail.planLabel || detail.planName || 'Plano atual';

  return (
    <section className="space-y-8">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative bg-slate-950 px-5 py-6 text-white sm:px-6 lg:px-8">
          <div className="absolute right-0 top-0 h-40 w-40 translate-x-16 -translate-y-16 rounded-full bg-indigo-500/20 blur-2xl" />
          <div className="absolute bottom-0 left-1/2 h-28 w-28 rounded-full bg-sky-400/10 blur-2xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-200">Assinatura</p>
                <BillingStatusBadge status={detail.status} />
              </div>
              <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">Minha Assinatura</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                Acompanhe plano, cobrança e limites operacionais da empresa ativa no WaiterCall.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 lg:shrink-0">
              {canChangePlan(detail.status) && (
                <button
                  type="button"
                  onClick={openChangePlan}
                  disabled={loadingPlans}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-sm transition hover:bg-indigo-50 disabled:opacity-60 sm:flex-none"
                >
                  <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
                  Alterar plano
                </button>
              )}
              <ContextHelpButton
                label="Ajuda sobre assinatura"
                className="border-white/10 bg-white/10 text-white hover:border-white/30 hover:bg-white/15 hover:text-white focus:ring-white"
                onClick={() => {
                  setHelpSectionKey('overview');
                  setIsHelpOpen(true);
                }}
              />
            </div>
          </div>
        </div>

        <div className="grid gap-0 divide-y divide-slate-200 lg:grid-cols-[1.2fr_0.8fr] lg:divide-x lg:divide-y-0">
          <div className="grid gap-4 p-5 sm:grid-cols-2 lg:p-6">
            <HeroMetric icon={CreditCardIcon} label="Plano contratado" value={planName} description={cycleLabel(detail.billingCycle)} />
            <HeroMetric icon={RocketLaunchIcon} label="Valor do plano" value={formatMoney(detail.priceAmount, detail.currency)} description={providerLabel(detail.provider)} />
            <HeroMetric
              icon={CalendarDaysIcon}
              label={detail.isWithinTrial ? 'Primeira cobrança' : 'Próxima cobrança'}
              value={formatDate(detail.nextDueAt)}
              description={daysUntilDue === null ? 'Data não informada' : daysUntilDue < 0 ? 'Vencimento ultrapassado' : `Em ${daysUntilDue} dia(s)`}
            />
            <HeroMetric icon={ShieldCheckIcon} label="Origem" value={originLabel(detail.origin)} description="Gestão da assinatura" />
          </div>

          <div className="p-5 lg:p-6">
            <div className={clsx('h-full rounded-3xl border p-5', toneStyles[statusInsight.tone])}>
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/70 shadow-sm">
                  <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold">{statusInsight.title}</p>
                  <p className="mt-2 text-sm leading-6 opacity-85">{statusInsight.description}</p>
                </div>
              </div>
              {detail.isWithinTrial && !isCancelled ? (
                <div className="mt-4 rounded-2xl bg-white/60 px-4 py-3 text-sm font-medium">
                  Avaliação gratuita até {formatDate(detail.trialEndsAt)}.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {(detail.status === 'past_due' || detail.status === 'suspended') && (
        <AlertBanner
          tone="warning"
          icon={ExclamationTriangleIcon}
          text={detail.status === 'suspended'
            ? 'Sua assinatura está suspensa. Regularize a cobrança para restabelecer o acesso.'
            : 'A assinatura está em atraso. Regularize a cobrança para evitar bloqueio do acesso.'}
        />
      )}

      {isCancelled && (
        <AlertBanner
          tone="muted"
          icon={XCircleIcon}
          text="Esta assinatura foi cancelada. Os dados serão mantidos por um período de retenção."
        />
      )}

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          eyebrow="Consumo do plano"
          title="Capacidade operacional"
          description="Monitore o uso de mesas e restaurantes antes que a operação encoste no limite contratado."
        />
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          <UsageMeter
            icon={Squares2X2Icon}
            label="Mesas ativas"
            used={detail.tablesUsed ?? 0}
            limit={detail.tableLimit}
            percent={tablePercent}
          />
          <UsageMeter
            icon={BuildingStorefrontIcon}
            label="Restaurantes ativos"
            used={detail.restaurantsUsed ?? 0}
            limit={detail.restaurantLimit}
            percent={restaurantPercent}
          />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <SectionHeader
            eyebrow="Detalhes"
            title="Dados da assinatura"
            description="Informações contratuais usadas para cobrança e controle de acesso."
          />
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <DetailTile label="Ativa desde" value={formatDate(detail.activatedAt)} />
            <DetailTile label="Início" value={formatDate(detail.startedAt)} />
            <DetailTile label="Ciclo de cobrança" value={cycleLabel(detail.billingCycle)} />
            <DetailTile label="Provedor" value={providerLabel(detail.provider)} />
            <DetailTile label="Origem" value={originLabel(detail.origin)} className="sm:col-span-2" />
          </div>
        </div>

        {!isCancelled ? (
          <div className="rounded-3xl border border-rose-200 bg-white p-5 shadow-sm sm:p-6">
            <SectionHeader
              eyebrow="Cancelamento"
              title="Zona sensível"
              description={detail.isWithinTrial
                ? 'Durante a avaliação gratuita, o cancelamento não gera cobrança.'
                : 'Ao cancelar, o acesso ao sistema será encerrado conforme a regra comercial.'}
            />
            {detail.isWithinTrial ? (
              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-800">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                <p>Avaliação gratuita até {formatDate(detail.trialEndsAt)}. O cancelamento não gera cobrança.</p>
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-300 bg-white px-4 py-2.5 text-sm font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50 disabled:opacity-50 sm:w-auto"
            >
              {cancelling && <Spinner className="h-4 w-4 border-rose-300 border-t-rose-600" />}
              Cancelar assinatura
            </button>
          </div>
        ) : null}
      </section>

      <ChangePlanDialog
        open={changePlanOpen}
        onClose={() => !changingPlan && !loadingPlans && setChangePlanOpen(false)}
        plans={availablePlans}
        selectedPlanId={selectedPlanId}
        setSelectedPlanId={setSelectedPlanId}
        loading={loadingPlans}
        submitting={changingPlan}
        onConfirm={confirmChangePlan}
      />

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Minha Assinatura"
        description="Revise plano, limites contratados, cobrança, troca de plano e cancelamento."
        sections={MY_SUBSCRIPTION_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </section>
  );
};

function SubscriptionLoadingState() {
  return (
    <section className="space-y-8">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-slate-950 px-5 py-6 sm:px-6 lg:px-8">
          <Skeleton className="h-4 w-28 bg-white/20" />
          <Skeleton className="mt-4 h-8 w-56 bg-white/20" />
          <Skeleton className="mt-3 h-4 w-full max-w-xl bg-white/20" />
        </div>
        <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4 lg:p-6">
          {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-24 rounded-2xl" />)}
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-44 rounded-3xl" />
        <Skeleton className="h-44 rounded-3xl" />
      </div>
    </section>
  );
}

function SubscriptionFeedbackState({ title, description, icon: Icon, tone, actionLabel, onAction }) {
  const danger = tone === 'danger';
  return (
    <section className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600">Assinatura</p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Minha Assinatura</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">Acompanhe seu plano, consumo e cobrança.</p>
      </div>
      <div className={clsx('rounded-3xl border p-6 text-center shadow-sm', danger ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50')}>
        <Icon className={clsx('mx-auto h-10 w-10', danger ? 'text-rose-500' : 'text-slate-400')} aria-hidden="true" />
        <h2 className={clsx('mt-4 text-base font-semibold', danger ? 'text-rose-900' : 'text-slate-950')}>{title}</h2>
        <p className={clsx('mx-auto mt-2 max-w-xl text-sm leading-6', danger ? 'text-rose-700' : 'text-slate-600')}>{description}</p>
        {actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-5 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">{eyebrow}</p>
      <h2 className="mt-2 text-lg font-semibold tracking-tight text-slate-950">{title}</h2>
      {description ? <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p> : null}
    </div>
  );
}

function HeroMetric({ icon: Icon, label, value, description }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-1 break-words text-lg font-semibold text-slate-950">{value ?? '-'}</p>
          {description ? <p className="mt-1 text-xs text-slate-500">{description}</p> : null}
        </div>
      </div>
    </div>
  );
}

function AlertBanner({ icon: Icon, text, tone }) {
  const isWarning = tone === 'warning';
  return (
    <div className={clsx('flex items-start gap-3 rounded-3xl border p-4', isWarning ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-700')} role={isWarning ? 'alert' : 'status'}>
      <Icon className={clsx('mt-0.5 h-5 w-5 shrink-0', isWarning ? 'text-amber-600' : 'text-slate-400')} aria-hidden="true" />
      <p className="text-sm leading-6">{text}</p>
    </div>
  );
}

function UsageMeter({ icon: Icon, label, used, limit, percent }) {
  const color = usageBarColor(percent);
  const cappedPercent = Math.min(Math.max(percent, 0), 100);
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-950">{label}</p>
            <p className="mt-1 text-sm text-slate-500">{used} de {limit} em uso</p>
          </div>
        </div>
        <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200">
          {percent}%
        </span>
      </div>
      <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-white ring-1 ring-slate-200" role="progressbar" aria-valuenow={cappedPercent} aria-valuemin={0} aria-valuemax={100}>
        <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${cappedPercent}%` }} />
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500">
        {percent >= 90 ? 'Consumo crítico: avalie trocar de plano antes de cadastrar novas unidades.' : percent >= 70 ? 'Consumo elevado: acompanhe a expansão da operação.' : 'Consumo dentro da margem operacional.'}
      </p>
    </div>
  );
}

function DetailTile({ label, value, className }) {
  return (
    <div className={clsx('rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3', className)}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-slate-950">{value ?? '-'}</p>
    </div>
  );
}

function ChangePlanDialog({ open, onClose, plans, selectedPlanId, setSelectedPlanId, loading, submitting, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-[1px]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-h-[92dvh] w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="border-b border-slate-200 bg-slate-50 px-6 py-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Plano</p>
            <Dialog.Title className="mt-2 text-lg font-semibold text-slate-950">Alterar assinatura</Dialog.Title>
            <p className="mt-1 text-sm leading-6 text-slate-600">Selecione o plano desejado. A alteração entra em vigor imediatamente.</p>
          </div>

          <div className="max-h-[58dvh] overflow-y-auto px-5 py-5 sm:px-6">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((item) => <Skeleton key={item} className="h-24 rounded-2xl" />)}
              </div>
            ) : plans.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-10 text-center">
                <p className="text-sm font-semibold text-slate-900">Nenhum plano disponível</p>
                <p className="mt-1 text-sm text-slate-500">Não há opções liberadas para alteração neste momento.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {plans.map((plan) => {
                  const isCurrent = plan.isCurrent;
                  const isSelected = selectedPlanId === plan.id;
                  return (
                    <button
                      type="button"
                      key={plan.id}
                      disabled={isCurrent}
                      onClick={() => setSelectedPlanId(plan.id)}
                      className={clsx(
                        'w-full rounded-2xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
                        isCurrent && 'cursor-default border-slate-200 bg-slate-50 opacity-70',
                        !isCurrent && isSelected && 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500',
                        !isCurrent && !isSelected && 'border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50'
                      )}
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-950">
                            {plan.landingLabel || plan.name}
                            {isCurrent ? <span className="ml-2 text-xs font-medium text-slate-500">Atual</span> : null}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                            <span className="rounded-full bg-slate-100 px-2 py-1">{cycleLabel(plan.billingCycle)}</span>
                            <span className="rounded-full bg-slate-100 px-2 py-1">{plan.tableLimit} mesas</span>
                            <span className="rounded-full bg-slate-100 px-2 py-1">{plan.restaurantLimit} restaurante(s)</span>
                          </div>
                        </div>
                        <p className="text-lg font-semibold text-slate-950">{formatMoney(plan.priceAmount, plan.currency)}</p>
                      </div>
                      {plan.landingCaption ? <p className="mt-3 text-sm leading-6 text-slate-500">{plan.landingCaption}</p> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!selectedPlanId || submitting}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:opacity-50"
            >
              {submitting && <Spinner className="h-4 w-4 border-white/30 border-t-white" />}
              Confirmar alteração
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function Spinner({ className }) {
  return <div className={clsx('animate-spin rounded-full border-2', className)} />;
}

export default MySubscriptionPage;
